import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { DwellingContract } from '../../model/dwelling-contract';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractCommandHandlerResolver } from '../../service/command/contract-command-handler-resolver.service';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { DwellingContractCommandFactory } from '../../service/command/factory/dwelling-contract-command-factory.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { PrintParkingContractFormCommandHandler } from '../../service/command/print-parkingcontract-command-handler';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { BuildingService } from '../../service/data/building.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { DwellingContractService } from './../../service/data/dwelling-contract.service';
import { ContractManagerPagedBase } from '../base/contract-manager-paged';
import { CustomPropertyVisibilityType, RentalObjectCategory, SubleaseStatus, SubleaseStatusToString } from '../../model/const/enums';
import { CustomPropertyService } from '../../../rent/service/data/custom-property.service';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { PropertyService } from '../../service/data/property.service';
import { CompanyService } from '../../service/data/company.service';
import { ArticleGroupService } from '../../../rent/service/data/article-group.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import { getTranslation } from '../../../core/helper/translation';
import { AdjustmentKind } from '../../../rent/model/const/enums';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { ProfileService } from '../../../common/service/data/profile.service';
import { DwellingLeaseAgreementListService } from '../../service/data/lease-agreement-list.service';
import { LanguageService } from '../../../core/service/language.service';

@Component({
  selector: 'app-dwelling-contract-manager',
  templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
  styleUrls: ['./../../../core/component/object-manager/object-manager.template.scss'],
  providers: [
    ApproveContractCommandHandler,
    ContractCommandHandlerResolver,
    ShowLeaseAgreementInvoicesCommandHandler,
    TerminateContractCommandHandler,
    VoidContractCommandHandler,
    ESignLeaseAgreementContractCommandHandler,
    ESignKeyRecieptCommandHandler,
    WithdrawApprovalCommandHandler,
    WithdrawTerminationCommandHandler,
    PrintContractCommandHandler,
    PrintParkingContractFormCommandHandler,
    DwellingLeaseAgreementListService,
    { provide: ContractService, useExisting: DwellingContractService },
    { provide: CommandFactoryService, useClass: DwellingContractCommandFactory },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DwellingContractManagerComponent extends ContractManagerPagedBase<DwellingContract> {
  constructor(
    activatedRoute: ActivatedRoute,
    columnDefinitionService: ColumnDefinitionService,
    dispatcher: CommandDispatcher,
    handlerResolver: ContractCommandHandlerResolver,
    userAccount: UserAccountService,
    service: DwellingContractService,
    errorHandler: MessageService,
    navigationService: NavigationService,
    private _buildingService: BuildingService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly propertyService: PropertyService,
    private readonly companyService: CompanyService,
    private readonly _articleGroupService: ArticleGroupService,
    private readonly _articleService: ArticleService,
    private readonly _languageService: LanguageService,
    private readonly _cdf: ColumnDefinitionFactory,
    private readonly _profileService: ProfileService,
    leaseAgreementListService: DwellingLeaseAgreementListService
  ) {
    super(
      service,
      dispatcher,
      handlerResolver,
      errorHandler,
      navigationService,
      userAccount,
      companyService,
      propertyService,
      activatedRoute,
      leaseAgreementListService
    );
    this.title = 'LEASE_AGREEMENT.DWELLING_LIST_HEADER';
    this.preferencesKey = 'rent.dwelling-contract.list';
    this.showExcelExportButton = true;
    this.excelFileName = 'Hyreskontrakt';
    this.excelContext = 'DwellingContract';
    this.columnDefinition = columnDefinitionService.create('DwellingContract');
    this.excelColumnDefinition = [...this.columnDefinition];
    this._initDynamicArticleColumns();
    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Messages, Features.ManageESignatures)) {
            this.addContextMenuItemDefinition('COMMON.COMMAND.ESIGN_KEY_RECIEPT', leaseAgreementCommand.esignKeyReciept);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.PRINT', leaseAgreementCommand.print);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }
        },
      })
    );
  }

  private _initDynamicArticleColumns(): void {
    this._articleGroupService.getList().subscribe(groups => {
      const tran = this._languageService.getCurrentTranslations();
      const groupYearText = getTranslation(tran, 'COMMON.YEAR').toLowerCase();
      const baseRentText = getTranslation(tran, 'COMMON.RENTAL_OBJECT');
      for (const g of groups) {
        this.excelColumnDefinition.push(
          this._cdf.create(`${baseRentText} ${g.name} ${groupYearText}`, `articleGroup.${g.id}`, ColumnVisibility.optional)
        );
      }
      this.excelColumnDefinition.push(
        this._cdf.create('CONST.DIRECT_DEBIT_FROM', 'directDebitFrom', ColumnVisibility.optional),
        this._cdf.create('CONST.DIRECT_DEBIT_STATUS_TERMINATED', 'directDebitTerminated', ColumnVisibility.optional),
        this._cdf.create('ADDRESS_FORM.RECIPIENT_LABEL', 'adressRowOne', ColumnVisibility.optional),
        this._cdf.create('ADDRESS_FORM.ADDRESS_ROW2', 'adressRowTwo', ColumnVisibility.optional),
        this._cdf.create('ADDRESS_FORM.STREET_LABEL', 'addressStreet', ColumnVisibility.optional),
        this._cdf.create('ADDRESS_FORM.POSTAL_CODE_LABEL', 'postalCode', ColumnVisibility.optional),
        this._cdf.create('ADDRESS_FORM.CITY_LABEL', 'city', ColumnVisibility.optional),
        this._cdf.create('COMMON.EMAIL_INVOICING', 'emailInvoicing', ColumnVisibility.optional)
      );
      this.addArticleColumns();
      this.addUseBrfColumn();
    });
  }

  private addUseBrfColumn(): void {
    this._profileService.getConfig().subscribe(profile => {
      if (profile.useBrf) {
        const tran = this._languageService.getCurrentTranslations();
        this.excelColumnDefinition.push(this._cdf.create('COOPERATIVE_HOUSING.SHARE', 'condominiumShare', ColumnVisibility.optional));
        this.excelColumnDefinition.push(
          this._cdf.create('COOPERATIVE_HOUSING.ECONOMICAL_PLAN_NUMBER', 'economicalPlanNumber', ColumnVisibility.optional)
        );
      }
    });
  }

  private addArticleColumns(): void {
    this._articleService.getList().subscribe(articles => {
      articles
        .filter(a => a.dwellingCategory)
        .forEach(a => {
          const existingBindings = new Set(this.excelColumnDefinition.map(c => (c as any).binding));
          const articleIdentifier = `article.${a.id}`;

          if (!existingBindings.has(articleIdentifier))
            this.excelColumnDefinition.push(this._cdf.create(`${a.code} ${a.caption}`, articleIdentifier, ColumnVisibility.optional));
        });
    });
  }
  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  addFilterDefinitions(definition: any) {
    this._customPropertyService.getList().subscribe(customProperties => {
      super.addFilterDefinitions(definition);
      this.filterDefinition.push(
        new FilterDefinitionItem(
          'COMMON.BUILDING',
          'building',
          definition.building,
          this._buildingService,
          definition.selectedFields.includes('building')
        ),
        new FilterDefinitionItem(
          'SUBLEASE_REQUEST.SUBLEASE',
          'subleaseStatus',
          definition.subleaseStatus,
          [
            { caption: SubleaseStatusToString(SubleaseStatus.None), value: SubleaseStatus.None },
            { caption: SubleaseStatusToString(SubleaseStatus.Valid), value: SubleaseStatus.Valid },
            { caption: SubleaseStatusToString(SubleaseStatus.Coming), value: SubleaseStatus.Coming },
            { caption: SubleaseStatusToString(SubleaseStatus.Expired), value: SubleaseStatus.Expired },
          ],
          definition.selectedFields.includes('subleaseStatus')
        )
      );
      const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
        customProperties,
        definition,
        CustomPropertyVisibilityType.Company |
          CustomPropertyVisibilityType.Dwelling |
          CustomPropertyVisibilityType.Property |
          CustomPropertyVisibilityType.Building
      );
      this._ch.createCustomPropertyColumnDefinition(
        customProperties,
        this.columnDefinition,
        CustomPropertyVisibilityType.Company |
          CustomPropertyVisibilityType.Dwelling |
          CustomPropertyVisibilityType.Property |
          CustomPropertyVisibilityType.Building
      );
      this._ch.createCustomPropertyColumnDefinition(
        customProperties,
        this.excelColumnDefinition,
        CustomPropertyVisibilityType.Company |
          CustomPropertyVisibilityType.Dwelling |
          CustomPropertyVisibilityType.Property |
          CustomPropertyVisibilityType.Building
      );
      this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
    });
  }
}
